/** DateRangeParamControl — static/dynamic mode toggle for param_date_range parameters. */

import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import type { DateRangeParamValue } from "@/types/schedule";

export interface DateRangeParamControlProps {
  paramName: string;
  label: string;
  helpText?: string;
  value: DateRangeParamValue;
  onChange: (value: DateRangeParamValue) => void;
  error?: string;
}

const DEFAULT_DYNAMIC: DateRangeParamValue = {
  mode: "dynamic",
  offset: { amount: 7, unit: "days" },
};

const DEFAULT_STATIC: DateRangeParamValue = {
  mode: "static",
  value: { from: "", to: "" },
};

export default function DateRangeParamControl({
  paramName,
  label,
  helpText,
  value,
  onChange,
  error,
}: DateRangeParamControlProps) {
  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: "static" | "dynamic" | null) => {
    if (newMode === null) return;
    if (newMode === "dynamic") {
      onChange(DEFAULT_DYNAMIC);
    } else {
      onChange(DEFAULT_STATIC);
    }
  };

  const handleStaticFromChange = (from: string) => {
    if (value.mode === "static") {
      onChange({ mode: "static", value: { from, to: value.value.to } });
    }
  };

  const handleStaticToChange = (to: string) => {
    if (value.mode === "static") {
      onChange({ mode: "static", value: { from: value.value.from, to } });
    }
  };

  const handleAmountChange = (rawValue: string) => {
    if (value.mode !== "dynamic") return;
    const parsed = parseInt(rawValue, 10);
    const amount = Number.isNaN(parsed) ? 1 : Math.max(1, Math.min(365, parsed));
    onChange({ mode: "dynamic", offset: { amount, unit: value.offset.unit } });
  };

  const handleUnitChange = (unit: "days" | "weeks" | "months") => {
    if (value.mode === "dynamic") {
      onChange({ mode: "dynamic", offset: { amount: value.offset.amount, unit } });
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
        {label}
      </Typography>

      <ToggleButtonGroup
        value={value.mode}
        exclusive
        onChange={handleModeChange}
        size="small"
        sx={{ mb: 2 }}
        aria-label={`${paramName} mode`}
      >
        <ToggleButton value="static">Static</ToggleButton>
        <ToggleButton value="dynamic">Dynamic</ToggleButton>
      </ToggleButtonGroup>

      {value.mode === "static" && (
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={value.value.from}
            onChange={(e) => handleStaticFromChange(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            error={!!error}
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={value.value.to}
            onChange={(e) => handleStaticToChange(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            error={!!error}
            fullWidth
          />
        </Box>
      )}

      {value.mode === "dynamic" && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            label="Amount"
            type="number"
            size="small"
            value={value.offset.amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            slotProps={{ htmlInput: { min: 1, max: 365, step: 1 } }}
            error={!!error}
            sx={{ width: 120 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id={`${paramName}-unit-label`}>Unit</InputLabel>
            <Select
              labelId={`${paramName}-unit-label`}
              value={value.offset.unit}
              onChange={(e) => handleUnitChange(e.target.value as "days" | "weeks" | "months")}
              label="Unit"
            >
              <MenuItem value="days">days</MenuItem>
              <MenuItem value="weeks">weeks</MenuItem>
              <MenuItem value="months">months</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            from run date
          </Typography>
        </Box>
      )}

      {(helpText || error) && (
        <FormHelperText error={!!error} sx={{ mt: 1 }}>
          {error || helpText}
        </FormHelperText>
      )}
    </Box>
  );
}
